import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";
import { PageToolbar, UnsplashCredit, usePageTranslation } from "@/components/PageToolbar";

const INTRO = {
  title: "The Editorial Desk",
  sub: "Original reporting and analysis on world cinema — clearly labeled, carefully sourced, editorially independent.",
};

export default function News() {
  const [articles, setArticles] = useState([]);
  const [tags, setTags] = useState([]);
  const [params, setParams] = useSearchParams();
  const activeTag = params.get("tag") || "";
  const blocks = useMemo(() => INTRO, []);
  const { lang, tr, busy } = usePageTranslation(blocks, "news-intro");

  useEffect(() => {
    api.get("/articles", { params: activeTag ? { tag: activeTag } : {} }).then((r) => setArticles(r.data));
  }, [activeTag]);

  useEffect(() => {
    api.get("/articles/tags").then((r) => setTags(r.data));
  }, []);

  return (
    <main data-testid="news-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <p className="eyebrow">News, Reviews & Interviews</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl mt-2 text-[#1c1512]">{tr("title", INTRO.title)}</h1>
      <p className="text-[#6b5d51] mt-3 max-w-2xl">{tr("sub", INTRO.sub)}</p>
      <div className="mt-5">
        <PageToolbar speakText={`${tr("title", INTRO.title)}. ${tr("sub", INTRO.sub)}`} busy={busy} lang={lang} />
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {activeTag && (
          <button data-testid="clear-tag-button" onClick={() => setParams({})} className="flex items-center gap-1 bg-[#7d1a1a] text-[#f6f1e7] rounded-full px-3 py-1 text-xs font-medium">
            {activeTag} <X className="w-3 h-3" />
          </button>
        )}
        {tags.filter((t) => t !== activeTag).map((t) => (
          <button
            key={t}
            data-testid={`tag-chip-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => setParams({ tag: t })}
            className="bg-[#fbf8f1] border border-[#d8ccb8] rounded-full px-3 py-1 text-xs hover:border-[#7d1a1a] hover:text-[#7d1a1a] transition-colors"
          >
            {t}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-8 mt-10">
        {articles.map((a, i) => (
          <Link key={a.id} to={`/news/${a.id}`} data-testid={`article-card-${a.id}`} className={`group poster-card bg-[#fbf8f1] border border-[#d8ccb8] ${i === 0 ? "md:col-span-2 md:grid md:grid-cols-2" : ""}`}>
            {a.photo && (
              <div className={`overflow-hidden bg-[#171210] ${i === 0 ? "aspect-[16/10] md:aspect-auto md:h-full" : "aspect-[16/9]"}`}>
                <img src={a.photo.url} alt={a.photo.alt} className="w-full h-full object-cover" loading="lazy" />
              </div>
            )}
            <div className="p-6">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="bg-[#7d1a1a] text-[#f6f1e7] font-mono-label text-[10px] tracking-[0.18em] uppercase px-2 py-1">{a.category}</span>
                {a.ai_generated && (
                  <span data-testid={`ai-badge-${a.id}`} className="flex items-center gap-1 font-mono-label text-[9px] tracking-[0.12em] uppercase text-[#b06810]">
                    <Sparkles className="w-3 h-3" /> AI-assisted
                  </span>
                )}
                <span className="font-mono-label text-[10px] tracking-wide text-[#8a7c6d]">{a.date}</span>
              </div>
              <h2 className={`font-display font-bold mt-3 leading-snug group-hover:text-[#7d1a1a] transition-colors ${i === 0 ? "text-3xl" : "text-xl"}`}>{a.title}</h2>
              <p className="text-[#6b5d51] mt-3">{a.excerpt}</p>
              <p className="font-mono-label text-[10px] tracking-wide text-[#8a7c6d] mt-4">By {a.author}</p>
              {a.photo && (
                <span className="block font-mono-label text-[10px] tracking-wide text-[#8a7c6d] mt-2">
                  Photo: {a.photo.photographer} / Unsplash
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </main>
  );
}
